import type { GridFilterInputValueProps } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';

import {
  isInvertedDateRange,
  normalizeDateBetweenValue,
  type DateBetweenValue,
} from '../utils/dateBetweenFilterValue';

// MUI's filterDebounceMs default — GridFilterInputDate applies edits on the
// same delay, and the grid doesn't override it.
const FILTER_DEBOUNCE_MS = 150;

export type UseDateBetweenFilterInputArgs = Pick<GridFilterInputValueProps, 'item' | 'applyValue'>;

/**
 * Logic for the results grid's `between` filter input, mirroring MUI's
 * GridFilterInputDate: local state tracks every keystroke while applyValue is
 * debounced — a native date input reports intermediate values per typed digit
 * (year "2" → "0002"), and applying each one churns the filter model, which
 * resets the input mid-edit. Half-filled and inverted ranges still reach the
 * model — the field state must persist while the user edits — but the shared
 * serializer keeps them off the wire, so no request fires until the range is
 * valid.
 */
export const useDateBetweenFilterInput = ({ item, applyValue }: UseDateBetweenFilterInputArgs) => {
  const [[start, end], setRange] = useState<DateBetweenValue>(() =>
    normalizeDateBetweenValue(item.value)
  );
  const applyTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Sync from the model (operator switch, panel reset), as GridFilterInputDate does.
  useEffect(() => {
    setRange(normalizeDateBetweenValue(item.value));
  }, [item.value]);

  useEffect(() => () => clearTimeout(applyTimeout.current), []);

  const applyRange = useCallback(
    (next: DateBetweenValue) => {
      setRange(next);
      clearTimeout(applyTimeout.current);
      applyTimeout.current = setTimeout(
        () => applyValue({ ...item, value: next }),
        FILTER_DEBOUNCE_MS
      );
    },
    [applyValue, item]
  );

  const onStartChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => applyRange([event.target.value, end]),
    [applyRange, end]
  );

  const onEndChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => applyRange([start, event.target.value]),
    [applyRange, start]
  );

  const hasRangeError = isInvertedDateRange([start, end]);

  return { start, end, hasRangeError, onStartChange, onEndChange };
};
